/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import PersonStructuredData from '@apps/components/PersonStructuredData';
import SEOLocaleList from '@apps/components/SEOLocaleList';
import { BASE_URL } from '@apps/config';
import getNewsListing from '@apps/config/API/listing.config';
import { getNewCarById } from '@apps/config/API/new-car.config';
import NewsListingScreen from '@apps/screens/NewsListing';
import { localeConversions } from '@apps/utils/locale-conversion';
import { Breadcrumb } from '@stack/ui/components';
import { BreadCrumbOptions } from '@stack/ui/components/Breadcrumb/props';
import { NextPage, NextPageContext } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import useTranslation from 'next-translate/useTranslation';

/**
 * ===========================
 * MAIN
 * ===========================
 */
const Page: NextPage = (props) => {
  const { settings, webView, authorDetail } = props;
  const router = useRouter();
  const { lang, t } = useTranslation('common');

  const carBrand = settings.car_brand_featured;
  const pageSize = 40;
  const [data, setData] = useState<any>();
  const [loading, setloading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [cars, setCars] = useState();

  const currentPath = router?.query?.author;
  const author = data?.author_detail;

  const fetchData = async (pageNumber: number) => {
    try {
      const author = router?.query?.author;

      setloading(true);
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth' as any,
        });
      }, 0);

      const res = await getNewsListing({
        pageSize,
        page: pageNumber,
        author: author as string,
      });
      const newEvCar = await getNewCarById({ id: 4 });
      const newCars = newEvCar?.data?.find((car: any) => {
        return car.new_car_feature_id === 4;
      });
      setCars(newCars);
      if (res.data.length < 1) {
        router.replace(webView ? '/webview/404' : '/404');
        return;
      }
      setData(res);
      setloading(false);
    } catch (err) {
      console.log('err', err);
    }
  };

  const news = data;

  useEffect(() => {
    const { page } = router.query;
    setCurrentPage(parseInt(page as string) || 1);
    fetchData(parseInt(page as string) || 1);
  }, [router?.asPath, router?.locale]);

  const breadcrumbOpt: BreadCrumbOptions[] = [
    {
      title: t('news'),
      url: webView ? '/webview' : '/',
    },
    {
      title: author?.name,
    },
  ];

  const currentUrl =
    lang === 'en'
      ? `${BASE_URL}${router?.asPath}`
      : `${BASE_URL}/${lang}${router?.asPath}`;
  const cleanedPath = currentUrl.split('?')[0].toLowerCase();
  const seoTitle = `News/Articles by ${
    author?.name ?? ''
  } | Author | Carz Automedia Malaysia`;
  const seoDescription = `Catch the latest car news and expert reviews published by ${
    author?.name ?? ''
  } at Carz Automedia Malaysia. Benefit from a professional view and expertise from years of experienced automotive journalists. Stay updated with all trends, updates, and happenings in the Malaysian automotive industry.`;

  return (
    <>
      <Head>
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:url" content={cleanedPath} />
      </Head>
      <NextSeo
        title={seoTitle}
        titleTemplate={seoTitle}
        description={seoDescription}
        canonical={cleanedPath}
      />
      <SEOLocaleList />
      {/* unprefixed for every locale so it matches `NewsArticle.author.url` */}
      <PersonStructuredData
        author={authorDetail ?? author}
        url={`${BASE_URL}/author/${String(currentPath ?? '').toLowerCase()}`}
      />
      <div>
        {author && <Breadcrumb options={breadcrumbOpt} />}
        <NewsListingScreen
          isAuthor={!!author}
          author={author}
          categorySlug={currentPath}
          data={news?.data}
          currentPage={currentPage}
          totalCount={news?.meta?.total ?? 0}
          pageSize={pageSize}
          carBrand={carBrand}
          newEvCar={cars}
          loading={loading}
          href={`/author/${currentPath}`}
        />
      </div>
    </>
  );
};

export async function getServerSideProps(ctx: NextPageContext) {
  const locale = ctx?.locale ?? 'en';
  const newLocale = localeConversions(locale);
  const author = (ctx?.query?.author as string) ?? '';
  const data = await getNewsListing({
    pageSize: 40,
    page: 1,
    locale: newLocale,
    author,
  });
  const hasData = data?.data?.length > 0;
  if (!hasData) {
    return { notFound: true };
  }
  return { props: { authorDetail: data?.author_detail ?? null } };
}

/**
 * ===========================
 * EXPORTS
 * ===========================
 */
export default Page;
